import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FullBackupCard } from "@/components/admin/FullBackupCard";
import { DatabaseBackup, Download, Loader2, RefreshCw, CloudUpload, Unplug, CheckCircle2, AlertTriangle } from "lucide-react";

const fmtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const driveTag = (d) => d?.status === "ok" ? { cls: "text-emerald-400", t: "no Drive" } : d?.status === "error" ? { cls: "text-red-400", t: "falhou no Drive" } : null;

function BackupRow({ b, idx }) {
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const dr = driveTag(b.drive);
  useEffect(() => {
    api.get("/admin/backups/shops", { params: { path: b.path } })
      .then(({ data }) => setShops(Array.isArray(data) ? data : []))
      .catch(() => setShops([]));
  }, [b.path]);
  const download = async () => {
    setBusy(true);
    try {
      const url = shopId ? "/admin/backups/download-shop" : "/admin/backups/download";
      const params = shopId ? { path: b.path, shop_id: shopId } : { path: b.path };
      const { data, headers } = await api.get(url, { params, responseType: "blob" });
      const u = URL.createObjectURL(data);
      const a = document.createElement("a");
      const cd = headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.href = u;
      a.download = m ? m[1] : b.path.split("/").pop();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`backup-row-${idx}`} className="flex items-center gap-3 px-4 py-3 border-t border-zinc-800 first:border-t-0 flex-wrap">
      <div className="flex-1 min-w-0">
        <div data-testid={`backup-date-${idx}`} className="text-zinc-100 font-semibold">{fmtBR(b.created_at)}</div>
        <div className="text-xs text-zinc-500 truncate">
          {b.path.split("/").pop()}{dr && <span className={`ml-2 ${dr.cls}`}>· {dr.t}</span>}
        </div>
      </div>
      <span data-testid={`backup-size-${idx}`} className="text-sm text-zinc-300 tabular-nums whitespace-nowrap">{fmtSize(b.size)}</span>
      {shops.length > 0 ? (
        <select data-testid={`backup-shop-${idx}`} value={shopId} onChange={(e) => setShopId(e.target.value)}
                className="h-9 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-zinc-200 px-2 max-w-[180px]">
          <option value="">Backup completo</option>
          {shops.map((s) => <option key={s.id} value={s.id}>{s.name || s.folder}</option>)}
        </select>
      ) : (
        <span className="text-[11px] text-zinc-600">backup antigo</span>
      )}
      <Button data-testid={`backup-download-${idx}`} size="sm" variant="outline" disabled={busy} onClick={download} className="bg-zinc-900 border-zinc-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Baixar
      </Button>
    </li>
  );
}

function DriveCard({ status, reload }) {
  const [busy, setBusy] = useState("");
  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/admin/backups/drive/connect");
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  const upload = async () => {
    setBusy("upload");
    try { const { data } = await api.post("/admin/backups/drive/upload-now"); toast.success(`Enviado ao Drive (${data.filename || "backup"})`); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const disconnect = async () => {
    if (!window.confirm("Desconectar o Google Drive? O backup local continua.")) { return; }
    setBusy("disc");
    try { await api.post("/admin/backups/drive/disconnect"); toast.success("Drive desconectado"); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const last = status?.last_upload;
  return (
    <div data-testid="drive-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4 md:p-5 space-y-3">
      <div className="flex items-center gap-2">
        <CloudUpload className="h-5 w-5 text-sky-400" />
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Google Drive</h2>
      </div>
      {!status?.configured ? (
        <p className="text-sm text-amber-300">Falta configurar <code className="font-mono">GOOGLE_CLIENT_ID</code> e <code className="font-mono">GOOGLE_CLIENT_SECRET</code> nos secrets.</p>
      ) : !status?.connected ? (
        <>
          <p className="text-sm text-zinc-400">Envie uma cópia dos backups para uma pasta "AutoOficina Backups" no seu Google Drive (apenas os 30 arquivos mais novos ficam lá).</p>
          <Button data-testid="drive-connect" onClick={connect} disabled={busy === "connect"} className="bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase">
            {busy === "connect" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />} Conectar Google Drive
          </Button>
          <p className="text-[11px] text-zinc-500">Redirecionamento usado: <code className="font-mono break-all">{status.redirect_uri}</code></p>
        </>
      ) : (
        <>
          <div className="text-sm text-zinc-200 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Conectado como <span data-testid="drive-email" className="font-bold">{status.email}</span></div>
          {last ? (
            <div data-testid="drive-last" className={`text-xs ${last.status === "ok" ? "text-emerald-400" : "text-red-400"}`}>
              {last.status === "ok" ? (<><CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> Último envio: {fmtBR(last.at)} — {last.filename}</>)
                                     : (<><AlertTriangle className="inline h-3.5 w-3.5 mr-1" /> Falhou em {fmtBR(last.at)}: {last.error}</>)}
            </div>
          ) : <div className="text-xs text-zinc-500">Nenhum envio ainda. O próximo sai às 3h ou use o botão abaixo.</div>}
          <div className="flex flex-wrap gap-2">
            <Button data-testid="drive-upload-now" onClick={upload} disabled={busy === "upload"} className="bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase">
              {busy === "upload" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />} Enviar agora ao Drive
            </Button>
            <Button data-testid="drive-disconnect" variant="outline" onClick={disconnect} disabled={busy === "disc"} className="bg-zinc-900 border-zinc-800 text-zinc-200">
              {busy === "disc" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />} Desconectar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminBackups() {
  const [list, setList] = useState(null);
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [running, setRunning] = useState(false);
  const [sp, setSp] = useSearchParams();
  const load = useCallback(() => {
    api.get("/admin/backups").then(({ data }) => setList(data)).catch((e) => { toast.error(formatApiError(e)); setList([]); });
    api.get("/admin/backups/drive/status").then(({ data }) => setStatus(data)).catch(() => setStatus({}));
    api.get("/admin/backups/health").then(({ data }) => setHealth(data)).catch(() => setHealth(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const d = sp.get("drive");
    if (d === "connected") { toast.success("Google Drive conectado"); setSp({}, { replace: true }); }
    else if (d === "error") { toast.error(sp.get("msg") || "Falha ao conectar o Drive"); setSp({}, { replace: true }); }
  }, [sp, setSp]);
  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/backups/run"); toast.success("Backup feito com sucesso"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };
  return (
    <div data-testid="admin-backups-page" className="max-w-3xl space-y-6">
      {[["local", "Backup diário"], ["drive", "Envio ao Google Drive"]].map(([k, lbl]) => {
        const h = health?.[k];
        if (!h || (h.fail_streak || 0) < 2) return null;
        return (
          <div key={k} data-testid={`backup-fail-banner-${k}`} className="rounded-xl border border-red-500/60 bg-red-600/20 px-4 py-3 text-sm text-red-100">
            <b>{lbl} falhou {h.fail_streak} dias seguidos.</b> {h.last_error ? `Último erro: ${h.last_error}.` : ""} O aviso some quando o próximo backup der certo.
          </div>
        );
      })}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-zinc-100 flex items-center gap-3"><DatabaseBackup className="h-8 w-8 text-green-brand" /> Backups</h1>
          <p className="text-sm text-zinc-400 mt-1">Cópia automática do banco todo dia às 3h (Brasília), guardada no armazenamento do app e no Google Drive quando conectado. Cada backup tem uma pasta por oficina para baixar separadamente.</p>
        </div>
        <Button data-testid="backup-run-now" onClick={runNow} disabled={running} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} {running ? "Fazendo backup..." : "Fazer backup agora"}
        </Button>
      </div>
      <DriveCard status={status} reload={load} />
      <FullBackupCard />
      {list === null ? (
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      ) : list.length === 0 ? (
        <div data-testid="backups-empty" className="rounded-xl border border-dashed border-zinc-700 p-6 text-zinc-400 text-sm">
          Ainda não há nenhuma cópia. A primeira sai automaticamente às 3h da manhã (horário de Brasília) — ou clique em "Fazer backup agora".
        </div>
      ) : (
        <ul data-testid="backups-list" className="rounded-xl border border-zinc-800 bg-zinc-950/50">
          {list.map((b, i) => <BackupRow key={b.path} b={b} idx={i} />)}
        </ul>
      )}
    </div>
  );
}
